import {
  Account,
  AccountType,
  AddAccountRequest,
  ASSET_LIABILITY,
  AssetMap,
  CreateAccountAndNaturalPersonalRequest,
  CreateAccountsResponse,
  SetAssetAllocationRequest,
  SignAgreementsRequest,
  AccountDetail,
  GetIraDistributionConstraintsResponse,
  GetIraContributionConstraintsResponse,
  Portfolio,
  FinancialPlansState,
} from '@/util/planning-util';
import { WealthChartData } from '@/types/wealth';
import {
  ApexUserInformation,
  adaptNaturalPerson,
  adaptAccount,
} from '@/util/account-openning-util';
import { getItemWithExpiry } from '@/util/util';
import { message } from 'antd';
import { Amplify, ResourcesConfig } from 'aws-amplify';
import { ApiError, get, post, del, put, patch } from 'aws-amplify/api';
import { RcFile } from 'antd/es/upload';
import { components } from '@/util/apex-client-gen';
import {
  AggregatedFinancialData,
  BankAccountData,
  PlaidHoldingsData,
  PlaidTransactionsData,
} from '@/util/plaid-util';
import {
  GetBankRelationshipResponse,
  MakeDepositsRequest,
  MakeDepositsResponse,
  MakeWithdrawalsRequest,
  MakeWithdrawalsResponse,
} from '@/util/transfer-util';
import {
  AssignBankRelationshipResponse,
  BankLinkOptionsResponse,
  BankUnlinkCheckResponse,
} from '@/util/bank-link';
import { ApexAccount } from '@/types/wealth';
import { AUTH_COPY } from '@/contexts/authCopy';
import { isAmplifyError } from './amplify-api-error';
import { messageFromAmplifyFailure, readOkJson } from './apex-json';
import { isRecord } from './type-guards';
import {
  getIdentityApiAlias,
  getIdentityApiBaseUrl,
  getInvestorApiRuntimeContext,
  getPlaidApiAlias,
  getPlaidApiBaseUrl,
  resetAliasResolutionState,
} from './investor-api-config';

export type { AmplifyError } from './amplify-api-error';
export { isAmplifyError } from './amplify-api-error';
export { isRecord } from './type-guards';
export type { InvestorApiReadiness } from './investor-api-readiness';
export { getInvestorApiReadiness } from './investor-api-readiness';
export type { InvestorApiRuntimeContext } from './investor-api-config';
export {
  getIdentityApiAlias,
  getIdentityApiBaseUrl,
  getInvestorApiRuntimeContext,
  getPlaidApiAlias,
  getPlaidApiBaseUrl,
  resetAliasResolutionState,
} from './investor-api-config';

export type ClientBrandingProfile = {
  id: string;
  brand_id?: string;
  name: string;
  introduction: string;
  logo_uri: string;
  logo_light_uri?: string;
  text_logo_uri: string;
  light_text_logo_uri?: string;
  video_uri: string;
  logo_mark_uri: string;
  compact_logo_uri?: string;
  compact_logo_light_uri?: string;
  background_uri: string;
  landing_color: string;
  customize_color: string;
  name_color: string;
  apex_correspondent_id: string;
  alt_investment_enabled: boolean;
  co_branding: boolean;
  /**
   * Optional human-readable firm alias used in branded URLs
   * (e.g. /firm/clayvvest). Empty when the firm has not configured one.
   */
  alias?: string;
  /**
   * Default color theme applied to first-time users who have not selected a
   * personal theme preference. The backend resolves this to either `light` or
   * `dark` (a "system default" choice is resolved before being persisted).
   */
  default_theme?: 'light' | 'dark';
  enabled_agreements?: Array<{
    agreement_id: string;
    title: string;
    url: string;
    description?: string;
  }>;
};

export type RiaProfile = ClientBrandingProfile;

export type RiaBrandingProfile = Pick<
  RiaProfile,
  | 'name'
  | 'introduction'
  | 'logo_uri'
  | 'logo_light_uri'
  | 'text_logo_uri'
  | 'light_text_logo_uri'
  | 'video_uri'
  | 'logo_mark_uri'
  | 'compact_logo_uri'
  | 'compact_logo_light_uri'
  | 'background_uri'
  | 'landing_color'
  | 'co_branding'
  | 'default_theme'
> & {
  id?: string;
  brand_id: string;
  /**
   * Optional human-readable firm alias used in branded URLs
   * (e.g. /firm/clayvvest, /firm/clayvvest/signup). Empty string when
   * the firm has not configured one — the brand_id-based URL still works.
   */
  alias?: string;
};

export type ClientAgreementsToSignRecord = {
  client_id: string;
  agreement_id: string;
  url: string;
  title: string;
  description: string;
  enabled: boolean;
  created_at: string;
  last_updated_at: string;
  version: string;
};

export type ApexAgreementsToSignRecord = {
  account: ApexAccount;
  agreements: components['schemas']['Agreement'][];
};

export type ClientFeaturesRecord = {
  client_id: string;
  tier_id: string;
  april_enabled: boolean;
  ladder_enabled: boolean;
  estate_planning_enabled: boolean;
  apex_enabled: boolean;
  canvas_enabled: boolean;
};

export type GetClientProfile = {
  profile: RiaProfile | null;
  enabled_agreements: ClientAgreementsToSignRecord[];
  features: ClientFeaturesRecord | null;
};

export type InvestorSignupInfo = {
  ria_profile: RiaProfile;
};

export type CompletedInvestorSignup = {
  investor_id: string;
  firm_id: string;
  cognito_id: string;
  email: string;
};

export type CompleteInvestorSignupRequest = {
  token: string;
  cognito_id: string;
  email: string;
};

export type InvestorSignupSupportSignalRequest = {
  token: string;
  request_id?: string;
  completion_attempt_id?: string;
  normalized_error_fingerprint?: string;
  sentry_event_id?: string;
  sentry_issue_id?: string;
};

export type InvestorSignupSupportSignalResponse = {
  notified: boolean;
  request_id?: string | null;
  completion_attempt_id?: string | null;
};

export type AccountSetupSupportSignalRequest = {
  userId: string;
  email?: string;
  message?: string;
  request_id?: string;
  normalized_error_fingerprint?: string;
  sentry_event_id?: string;
  sentry_issue_id?: string;
  source?: string;
};

export type AccountSetupSupportSignalResponse = {
  notified: boolean;
  request_id?: string | null;
};

const getLocalInvestorSignupApiBasePath = (): string =>
  process.env.VITE_LOCAL_INVESTOR_SIGNUP_API_BASE_PATH || '';

export class InvestorSignupLookupError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'InvestorSignupLookupError';
    this.statusCode = statusCode;
  }
}

export const isInvestorSignupLookupError = (error: unknown): error is InvestorSignupLookupError => {
  return error instanceof InvestorSignupLookupError;
};

const unwrapPublicApiBody = <T>(body: string): T | null => {
  if (!body) {
    return null;
  }

  const responseData = JSON.parse(body) as T | { data: T };
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return responseData.data;
  }

  return responseData as T;
};

const parsePublicApiErrorMessage = (body: string, fallbackMessage: string): string => {
  if (!body) {
    return fallbackMessage;
  }

  try {
    const responseData = JSON.parse(body) as {
      detail?: string;
      message?: string;
    };

    if (typeof responseData.detail === 'string' && responseData.detail) {
      return responseData.detail;
    }
    if (typeof responseData.message === 'string' && responseData.message) {
      return responseData.message;
    }
  } catch (error) {
    console.error('Failed to parse public API error body', error);
  }

  return fallbackMessage;
};

const fetchInvestorSignupFromToken = async (token: string): Promise<InvestorSignupInfo> => {
  try {
    const localInvestorSignupApiBasePath = getLocalInvestorSignupApiBasePath();
    if (localInvestorSignupApiBasePath) {
      const queryString = new URLSearchParams({ token }).toString();
      const response = await fetch(
        `${localInvestorSignupApiBasePath}/public/investor_signup_from_token?${queryString}`,
      );

      if (!response.ok) {
        throw new InvestorSignupLookupError(
          parsePublicApiErrorMessage(
            await response.text(),
            `Investor signup lookup failed with status ${response.status}`,
          ),
          response.status,
        );
      }

      const signupInfo = unwrapPublicApiBody<InvestorSignupInfo>(await response.text());
      if (!signupInfo) {
        throw new InvestorSignupLookupError(
          'Investor signup lookup returned an empty response',
          502,
        );
      }

      return signupInfo;
    }

    const response = await get({
      apiName: 'ClientPortalAPI',
      path: `/public/investor_signup_from_token`,
      options: {
        queryParams: {
          token,
        },
      },
    });

    const result = await response.response;
    const statusCode =
      typeof (result as { statusCode?: unknown }).statusCode === 'number'
        ? ((result as { statusCode: number }).statusCode as number)
        : 200;
    const body = await result.body.text();

    if (statusCode >= 400) {
      throw new InvestorSignupLookupError(
        parsePublicApiErrorMessage(body, `Investor signup lookup failed with status ${statusCode}`),
        statusCode,
      );
    }

    const signupInfo = unwrapPublicApiBody<InvestorSignupInfo>(body);
    if (!signupInfo) {
      throw new InvestorSignupLookupError('Investor signup lookup returned an empty response', 502);
    }

    return signupInfo;
  } catch (error: unknown) {
    if (isInvestorSignupLookupError(error)) {
      throw error;
    }

    if (isAmplifyError(error)) {
      throw new InvestorSignupLookupError(
        error.response.statusText ||
          `Investor signup lookup failed with status ${error.response.status}`,
        error.response.status,
      );
    }

    console.error('Failed to get investor signup info from token', error);
    throw new InvestorSignupLookupError("We couldn't open this signup link right now");
  }
};

export const getRiaProfileFromInviteToken = async (
  inviteToken: string,
): Promise<RiaProfile | null> => {
  try {
    const response = await get({
      apiName: 'ClientPortalAPI',
      path: `/public/client_profile_from_token`,
      options: {
        queryParams: {
          token: inviteToken,
        },
      },
    });

    const result = await response.response;
    const body = await result.body.text();

    if (body) {
      const responseData = JSON.parse(body) as RiaProfile | { data: RiaProfile };
      return 'data' in responseData ? responseData.data : responseData;
    }

    return null;
  } catch (error: unknown) {
    console.error('Failed to get RIA profile from invite token', error);
    return null;
  }
};

export const getRiaProfileFromBrandId = async (
  brandId: string,
): Promise<RiaBrandingProfile | null> => {
  try {
    const response = await get({
      apiName: 'RiaAPI',
      path: `/public/client_branding`,
      options: {
        queryParams: {
          brand_id: brandId,
        },
      },
    });

    const result = await response.response;
    const body = await result.body.text();

    return unwrapPublicApiBody<RiaBrandingProfile>(body);
  } catch (error: unknown) {
    console.error('Failed to get RIA branding profile from brand id', error);
    return null;
  }
};

/**
 * Resolve a branded RIA profile from a route identifier that may be either a
 * firm alias (e.g. "clayvvest") or a brand id (with or without the
 * "brand-" prefix). The backend tries the alias lookup first and then falls
 * back to the brand-id lookup, so callers can drop the legacy
 * `brand-${firmId}` construction logic.
 */
export const getRiaProfileFromRouteId = async (
  routeId: string,
): Promise<RiaBrandingProfile | null> => {
  try {
    const response = await get({
      apiName: 'RiaAPI',
      path: `/public/client-branding-by-route`,
      options: {
        queryParams: {
          route_id: routeId,
        },
      },
    });

    const result = await response.response;
    const body = await result.body.text();

    return unwrapPublicApiBody<RiaBrandingProfile>(body);
  } catch (error: unknown) {
    console.error('Failed to get RIA branding profile from route id', error);
    return null;
  }
};

export const getInvestorSignupFromToken = async (token: string): Promise<InvestorSignupInfo> => {
  return fetchInvestorSignupFromToken(token);
};

export const getInvestorSignupRiaProfileFromToken = async (token: string): Promise<RiaProfile> => {
  const info = await getInvestorSignupFromToken(token);
  return info.ria_profile;
};

export const completeInvestorSignup = async ({
  token,
  cognito_id,
  email,
}: CompleteInvestorSignupRequest): Promise<CompletedInvestorSignup | null> => {
  const localInvestorSignupApiBasePath = getLocalInvestorSignupApiBasePath();
  if (localInvestorSignupApiBasePath) {
    const response = await fetch(
      `${localInvestorSignupApiBasePath}/public/investor_signup_complete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          cognito_id,
          email,
        }),
      },
    );

    const body = await response.text();
    if (!response.ok) {
      throw new Error(
        parsePublicApiErrorMessage(
          body,
          `Investor signup completion failed with status ${response.status}`,
        ),
      );
    }

    return unwrapPublicApiBody<CompletedInvestorSignup>(body);
  }

  const response = await post({
    apiName: 'ClientPortalAPI',
    path: '/public/investor_signup_complete',
    options: {
      body: {
        token,
        cognito_id,
        email,
      },
    },
  });

  const result = await response.response;
  const statusCode =
    typeof (result as { statusCode?: unknown }).statusCode === 'number'
      ? ((result as { statusCode: number }).statusCode as number)
      : 200;
  const body = await result.body.text();
  if (statusCode >= 400) {
    throw new Error(
      parsePublicApiErrorMessage(
        body,
        `Investor signup completion failed with status ${statusCode}`,
      ),
    );
  }

  return unwrapPublicApiBody<CompletedInvestorSignup>(body);
};

export const notifyInvestorSignupSupportSignal = async (
  request: InvestorSignupSupportSignalRequest,
): Promise<InvestorSignupSupportSignalResponse | null> => {
  const localInvestorSignupApiBasePath = getLocalInvestorSignupApiBasePath();
  if (localInvestorSignupApiBasePath) {
    const response = await fetch(
      `${localInvestorSignupApiBasePath}/public/investor_signup/support-signal`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    const body = await response.text();
    if (!response.ok) {
      throw new Error(
        parsePublicApiErrorMessage(
          body,
          `Investor signup support notification failed with status ${response.status}`,
        ),
      );
    }

    return unwrapPublicApiBody<InvestorSignupSupportSignalResponse>(body);
  }

  const response = await post({
    apiName: 'ClientPortalAPI',
    path: '/public/investor_signup/support-signal',
    options: {
      body: request,
    },
  });

  const result = await response.response;
  const body = await result.body.text();
  return unwrapPublicApiBody<InvestorSignupSupportSignalResponse>(body);
};

export const notifyAccountSetupSupportSignal = async ({
  userId,
  ...request
}: AccountSetupSupportSignalRequest): Promise<AccountSetupSupportSignalResponse | null> => {
  const response = await post({
    apiName: 'ClientPortalAPI',
    path: `/api/investor/${userId}/account-setup/support-signal`,
    options: {
      body: request,
    },
  });

  const result = await response.response;
  const body = await result.body.text();
  return unwrapPublicApiBody<AccountSetupSupportSignalResponse>(body);
};

/**
 * Get RIA profile for the current user
 * @returns RIA profile or null
 */
export const getRiaProfile = async (): Promise<GetClientProfile> => {
  try {
    const user = await getUserData();
    if (!user) {
      throw new Error('User not found');
    }

    const url = `/api/my-ria-profile/${user?.cognito_id}`;
    const response = await get({
      apiName: 'ClientPortalAPI',
      path: url,
    });

    const result = await response.response;
    const body = await result.body.text();

    if (body) {
      const responseData = JSON.parse(body) as GetClientProfile;
      return responseData;
    }

    return {
      profile: null,
      enabled_agreements: [],
      features: null,
    };
  } catch (error: unknown) {
    console.error('Failed to get RIA profile', error);
    return {
      profile: null,
      enabled_agreements: [],
      features: null,
    };
  }
};

export type SignAgreementsResponse = {
  agreement_ids: string[];
};

/**
 * Sign agreements for the current user
 * @param agreementIds Array of agreement IDs to sign
 * @returns success status
 */
export const signWavvestAgreements = async (
  agreementIds: string[],
): Promise<SignAgreementsResponse> => {
  try {
    const user = await getUserData();
    if (!user) {
      throw new Error('User not found');
    }

    const response = await post({
      apiName: 'ClientPortalAPI',
      path: `/api/customer/${user?.cognito_id}/agreements/sign`,
      options: {
        body: {
          agreement_ids: agreementIds,
        },
      },
    });

    const result = await response.response;
    const body = await result.body.text();

    if (body) {
      const responseData = JSON.parse(body) as SignAgreementsResponse;
      return responseData;
    }

    return {
      agreement_ids: [],
    };
  } catch (error: unknown) {
    console.error('Failed to sign agreements', error);
    return {
      agreement_ids: [],
    };
  }
};

export type MemberProfile = {
  id: string;
  client_id: string;
  name: string;
  email: string;
  role: 'member' | 'admin';
};

export type UserDataResponse = {
  advisor_cognito_id: string;
  client_id: string;
  customer_cognito_id: string;
  date_of_birth: string;
  email: string;
  name: string;
  advisor_info: MemberProfile;
};

export type UserDetailsResponse = {
  id: string;
  firm_id: string;
  client_id: string;
  name: string;
  email: string;
  phone_number: string | null;
  date_of_birth: string;
  advisor_cognito_id: string;
  cognito_id: string;
  identity_id: string | null;
  progress: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

type RawUserDetailsResponse = Omit<UserDetailsResponse, 'firm_id' | 'client_id'> & {
  firm_id?: string;
  client_id?: string;
};

export type FetchInvestorDetailsApiResponse = {
  investors?: RawUserDetailsResponse[];
  customers?: RawUserDetailsResponse[];
};

export type CreateRiskScoreRequest = {
  user_id: string;
  firm_id: string;
  risk_score: number;
  account_id?: string | null;
};

export type UpdateRiskScoreRequest = {
  user_id: string;
  firm_id: string;
  risk_score: number;
  account_id?: string | null;
};

export type GetRiskScoresQuery = {
  firm_id: string;
  user_id: string;
  account_id?: string;
};

export type RiskScoreRecord = {
  id: string;
  user_id: string;
  firm_id: string;
  account_id: string | null;
  risk_score: number;
};

export type GetRiskScoresApiResponse = {
  status: 'success' | string;
  message: string;
  data: RiskScoreRecord[] | RiskScoreRecord;
};

export async function createRiskScore(payload: CreateRiskScoreRequest): Promise<void> {
  const userId = payload.user_id?.trim();
  const firmId = payload.firm_id?.trim();
  const accountId = payload.account_id?.trim() || null;
  const riskScore = payload.risk_score;

  if (!userId) {
    throw new Error('user_id is required and cannot be empty');
  }
  if (!firmId) {
    throw new Error('firm_id is required and cannot be empty');
  }
  if (typeof riskScore !== 'number' || !Number.isFinite(riskScore) || riskScore <= 0) {
    throw new Error('risk_score must be a finite number greater than 0');
  }

  try {
    await post({
      apiName: 'ClientPortalAPI',
      path: '/api/risk-scores',
      options: {
        body: {
          user_id: userId,
          firm_id: firmId,
          account_id: accountId,
          risk_score: riskScore,
        },
      },
    }).response;
  } catch (error: unknown) {
    console.error('Failed to create risk score', error);
    throw error;
  }
}

/**
 * Update an existing risk score entry.
 * Uses Amplify API (IAM SigV4 signing) via configured API Gateway alias.
 * Omit account_id (or pass null) to update the initial profile risk score.
 */
export async function updateRiskScore(payload: UpdateRiskScoreRequest): Promise<void> {
  const userId = payload.user_id?.trim();
  const firmId = payload.firm_id?.trim();
  const accountId = payload.account_id?.trim() || null;
  const riskScore = payload.risk_score;

  if (!userId) {
    throw new Error('user_id is required and cannot be empty');
  }
  if (!firmId) {
    throw new Error('firm_id is required and cannot be empty');
  }
  if (typeof riskScore !== 'number' || !Number.isFinite(riskScore) || riskScore <= 0) {
    throw new Error('risk_score must be a finite number greater than 0');
  }

  try {
    await put({
      apiName: 'ClientPortalAPI',
      path: '/api/risk-scores',
      options: {
        body: {
          user_id: userId,
          firm_id: firmId,
          account_id: accountId,
          risk_score: riskScore,
        },
      },
    }).response;
  } catch (error: unknown) {
    console.error('Failed to update risk score', error);
    throw error;
  }
}

/**
 * Get risk scores by firm/user, optionally filtered by account.
 * Uses Amplify API (IAM SigV4 signing) via configured API Gateway alias.
 */
export async function getRiskScores(query: GetRiskScoresQuery): Promise<GetRiskScoresApiResponse> {
  const firmId = query.firm_id?.trim();
  const userId = query.user_id?.trim();
  const accountId = query.account_id?.trim();

  if (!firmId) {
    throw new Error('firm_id is required and cannot be empty');
  }
  if (!userId) {
    throw new Error('user_id is required and cannot be empty');
  }

  const queryParams: Record<string, string> = {
    firm_id: firmId,
    user_id: userId,
  };

  if (accountId) {
    queryParams.account_id = accountId;
  }

  try {
    const apiResponse = get({
      apiName: 'ClientPortalAPI',
      path: '/api/risk-scores',
      options: {
        queryParams,
      },
    });

    const response = await apiResponse.response;
    const bodyText = await response.body.text();

    if (!bodyText) {
      throw new Error('Empty response body from /api/risk-scores');
    }

    return JSON.parse(bodyText) as GetRiskScoresApiResponse;
  } catch (error: unknown) {
    console.error('Failed to get risk scores', error);
    throw error;
  }
}

export type FetchUserDetailsResult =
  | {
      kind: 'success';
      data: UserDetailsResponse;
    }
  | {
      kind:
        | 'NO_INVESTOR_RECORD'
        | 'PROFILE_SUPPORT_REQUIRED'
        | 'AUTH_REQUIRED'
        | 'INVALID_RESPONSE'
        | 'API_ERROR'
        | 'PARSE_ERROR';
      message: string;
      supportFingerprint?: string;
    };

const normalizeUserDetails = (profile: RawUserDetailsResponse): UserDetailsResponse => {
  const firmId = profile.firm_id || profile.client_id || '';
  const clientId = profile.client_id || firmId;
  return {
    ...profile,
    firm_id: firmId,
    client_id: clientId,
  };
};

const getInvestorDetailsRecords = (
  data: FetchInvestorDetailsApiResponse | undefined,
): RawUserDetailsResponse[] | null => {
  if (Array.isArray(data?.investors)) {
    return data.investors;
  }
  if (Array.isArray(data?.customers)) {
    return data.customers;
  }
  return null;
};

const PROFILE_NOT_LINKED_FINGERPRINT = 'investor_account_setup:profile_not_linked_after_sign_in';
const PROFILE_ACCESS_SUPPORT_FINGERPRINT = 'investor_account_setup:profile_access_requires_support';

const classifyInvestorProfileFailure = (
  message: string | undefined,
  statusCode?: number,
): FetchUserDetailsResult => {
  const fallbackMessage = message || AUTH_COPY.accountSetup.profileLoadFailed;

  if (statusCode === 401) {
    return {
      kind: 'AUTH_REQUIRED',
      message: AUTH_COPY.login.sessionExpired,
    };
  }

  if (statusCode === 403 || statusCode === 404 || statusCode === 409) {
    return {
      kind: 'PROFILE_SUPPORT_REQUIRED',
      message: fallbackMessage,
      supportFingerprint:
        statusCode === 404 ? PROFILE_NOT_LINKED_FINGERPRINT : PROFILE_ACCESS_SUPPORT_FINGERPRINT,
    };
  }

  const normalizedMessage = fallbackMessage.toLowerCase();
  if (normalizedMessage.includes('investor not found')) {
    return {
      kind: 'PROFILE_SUPPORT_REQUIRED',
      message: fallbackMessage,
      supportFingerprint: PROFILE_NOT_LINKED_FINGERPRINT,
    };
  }
  if (
    normalizedMessage.includes('unable to uniquely resolve investor profile') ||
    normalizedMessage.includes('forbidden')
  ) {
    return {
      kind: 'PROFILE_SUPPORT_REQUIRED',
      message: fallbackMessage,
      supportFingerprint: PROFILE_ACCESS_SUPPORT_FINGERPRINT,
    };
  }
  if (
    normalizedMessage.includes('unauthorized') ||
    normalizedMessage.includes('not authorized') ||
    normalizedMessage.includes('session expired')
  ) {
    return {
      kind: 'AUTH_REQUIRED',
      message: AUTH_COPY.login.sessionExpired,
    };
  }

  return {
    kind: 'API_ERROR',
    message: AUTH_COPY.accountSetup.profileLoadFailed,
  };
};

/**
 * get user details from API
 * @param userId user id
 * @returns FetchUserDetailsResult with user data on success or failure details
 */
export const fetchUserDetails = async (
  userId: string,
  // ): Promise<UserDataResponse | null> => {
): Promise<FetchUserDetailsResult> => {
  try {
    // const url = `/api/me/${userId}`;
    const url = `/api/investor/${userId}`;

    const apiResponse = get({
      apiName: 'ClientPortalAPI',
      path: url,
    });

    const response = await apiResponse.response;
    const statusCode =
      typeof (response as { statusCode?: unknown }).statusCode === 'number'
        ? ((response as { statusCode: number }).statusCode as number)
        : 200;
    const bodyText = await response.body.text();

    if (statusCode >= 400) {
      return classifyInvestorProfileFailure(
        parsePublicApiErrorMessage(
          bodyText,
          `Investor profile request failed with status ${statusCode}`,
        ),
        statusCode,
      );
    }

    if (bodyText) {
      try {
        const responseData = JSON.parse(bodyText) as {
          status: string;
          message: string;
          data: FetchInvestorDetailsApiResponse;
        };
        const investors = getInvestorDetailsRecords(responseData.data);

        if (responseData?.status === 'success' && investors && investors.length > 0) {
          return {
            kind: 'success',
            data: normalizeUserDetails(investors[0]),
          };
        }

        if (responseData?.status === 'success' && investors && investors.length === 0) {
          return {
            kind: 'NO_INVESTOR_RECORD',
            message: AUTH_COPY.accountSetup.finishing,
          };
        }

        if (responseData?.status === 'error') {
          return classifyInvestorProfileFailure(responseData.message);
        }

        return {
          kind: 'INVALID_RESPONSE',
          message: AUTH_COPY.accountSetup.profileLoadFailed,
        };
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        return {
          kind: 'PARSE_ERROR',
          message: AUTH_COPY.accountSetup.profileLoadFailed,
        };
      }
    }
    return {
      kind: 'INVALID_RESPONSE',
      message: AUTH_COPY.accountSetup.profileLoadFailed,
    };
  } catch (apiError) {
    console.error('failed to get client API user data:', apiError);
    if (isAmplifyError(apiError)) {
      return classifyInvestorProfileFailure(
        apiError.response.statusText ||
          `Investor profile request failed with status ${apiError.response.status}`,
        apiError.response.status,
      );
    }
    return {
      kind: 'API_ERROR',
      message: AUTH_COPY.accountSetup.profileLoadFailed,
    };
  }
};

export const completeAccountSetup = async (
  userId: string,
  inviteToken: string,
): Promise<FetchUserDetailsResult> => {
  try {
    const apiResponse = post({
      apiName: 'ClientPortalAPI',
      path: `/api/investor/${userId}/signup/complete`,
      options: {
        body: {
          invite_token: inviteToken,
        },
      },
    });

    const response = await apiResponse.response;
    const bodyText = await response.body.text();
    if (!bodyText) {
      return {
        kind: 'INVALID_RESPONSE',
        message: "We couldn't complete your signup. Please try again later.",
      };
    }

    const responseData = JSON.parse(bodyText) as {
      status: string;
      message: string;
      data: FetchInvestorDetailsApiResponse;
    };
    const investors = getInvestorDetailsRecords(responseData.data);
    if (responseData.status === 'success' && investors && investors.length > 0) {
      return {
        kind: 'success',
        data: normalizeUserDetails(investors[0]),
      };
    }

    return {
      kind: 'NO_INVESTOR_RECORD',
      message:
        responseData.message ||
        "We couldn't complete your signup. Please check your invitation link.",
    };
  } catch (apiError) {
    console.error('failed to complete signup:', apiError);
    return {
      kind: 'API_ERROR',
      message: "We couldn't complete your signup. Please check your invitation link and try again.",
    };
  }
};

export type UpdateCustomerProfilePayload = {
  name?: string;
  date_of_birth?: string;
  address?: string;
};

export type UpdateCustomerProfileResponse = {
  status: string;
  message: string;
  data: Record<string, unknown>;
};

type UpdateCustomerProfileOptions = {
  showSuccessMessage?: boolean;
};

/**
 * Update customer profile (name, date_of_birth, address).
 * At least one of name, date_of_birth, address must be provided.
 */
export const updateCustomerProfile = async (
  customerId: string,
  payload: UpdateCustomerProfilePayload,
  options: UpdateCustomerProfileOptions = {},
): Promise<{ success: true } | { success: false; message: string }> => {
  try {
    const apiResponse = put({
      apiName: 'ClientPortalAPI',
      path: `/api/customer/${customerId}`,
      options: { body: payload },
    });

    const response = await apiResponse.response;
    const bodyText = await response.body.text();

    if (bodyText) {
      const responseData = JSON.parse(bodyText) as UpdateCustomerProfileResponse;
      if (responseData.status === 'success') {
        if (options.showSuccessMessage ?? true) {
          message.success(responseData.message ?? 'Customer attributes updated successfully');
        }
        return { success: true };
      }
      return { success: false, message: responseData.message };
    }

    return { success: false, message: 'Failed to update profile' };
  } catch (apiError) {
    console.error('failed to update customer profile:', apiError);
    if (isAmplifyError(apiError) && apiError.response?.data) {
      const responseData = apiError.response.data as { message?: string };
      return {
        success: false,
        message: responseData.message ?? 'Failed to update profile',
      };
    }
    return { success: false, message: 'Failed to update profile' };
  }
};

export type InvestorProfilePayload = Record<string, unknown>;

export type InvestorProfilePayloadPatch = {
  expected_revision: string;
  idempotency_key: string;
  update: Record<string, unknown>;
};

export type InvestorProfilePayloadUpdateResult = {
  profile: InvestorProfilePayload;
  updated_paths: string[];
};

export const INVESTOR_PROFILE_CONFLICT_MESSAGE =
  'Profile data changed. Refresh the profile and try again.';

const getApiStatusCode = (response: { statusCode?: unknown }): number =>
  typeof response.statusCode === 'number' ? response.statusCode : 200;

export const fetchInvestorProfileV1 = async (): Promise<InvestorProfilePayload | null> => {
  try {
    const apiResponse = get({
      apiName: 'ClientPortalAPI',
      path: '/api/profile',
    });

    const response = await apiResponse.response;
    const statusCode = getApiStatusCode(response);
    const bodyText = await response.body.text();

    if (statusCode === 404) {
      return null;
    }

    if (statusCode >= 400) {
      throw new Error(
        parsePublicApiErrorMessage(
          bodyText,
          `Investor profile request failed with status ${statusCode}`,
        ),
      );
    }

    return unwrapPublicApiBody<InvestorProfilePayload>(bodyText);
  } catch (apiError) {
    console.error('failed to get investor profile payload:', apiError);
    throw apiError;
  }
};

export type ApexOnboardingPrefillPayload = Record<string, unknown>;

export const fetchApexOnboardingPrefill =
  async (): Promise<ApexOnboardingPrefillPayload | null> => {
    try {
      const apiResponse = get({
        apiName: 'ClientPortalAPI',
        path: '/api/apex/onboarding-prefill',
      });

      const response = await apiResponse.response;
      const statusCode = getApiStatusCode(response);
      const bodyText = await response.body.text();

      if (statusCode === 404) {
        return null;
      }

      if (statusCode >= 400) {
        throw new Error(
          parsePublicApiErrorMessage(
            bodyText,
            `Apex onboarding prefill request failed with status ${statusCode}`,
          ),
        );
      }

      return unwrapPublicApiBody<ApexOnboardingPrefillPayload>(bodyText);
    } catch (apiError) {
      console.error('failed to get Apex onboarding prefill:', apiError);
      throw apiError;
    }
  };

export const updateInvestorProfileV1 = async (
  payload: InvestorProfilePayloadPatch,
): Promise<InvestorProfilePayloadUpdateResult> => {
  try {
    const apiResponse = patch({
      apiName: 'ClientPortalAPI',
      path: '/api/profile',
      options: { body: payload },
    });

    const response = await apiResponse.response;
    const statusCode = getApiStatusCode(response);
    const bodyText = await response.body.text();

    if (statusCode === 409) {
      throw new Error(INVESTOR_PROFILE_CONFLICT_MESSAGE);
    }

    if (statusCode >= 400) {
      throw new Error(
        parsePublicApiErrorMessage(
          bodyText,
          `Investor profile update failed with status ${statusCode}`,
        ),
      );
    }

    const responseData = unwrapPublicApiBody<InvestorProfilePayloadUpdateResult>(bodyText);
    if (!responseData) {
      throw new Error('Investor profile update returned an empty response');
    }

    return responseData;
  } catch (apiError) {
    console.error('failed to update investor profile payload:', apiError);
    throw apiError;
  }
};

/**
 * get user data from local storage
 * @returns user data or null
 */
export const getUserData = async () => {
  try {
    const userStr = getItemWithExpiry('user');
    if (userStr) {
      // const userData = JSON.parse(userStr) as UserDataResponse;
      const userData = JSON.parse(userStr) as UserDetailsResponse;

      // TODO: get user details from fetchUserDetails function when necessary
      if (userData) {
        return userData;
      } else {
        message.error('can not get user data, please login again');
        window.location.href = '/';
      }
    }
  } catch (err) {
    message.error('can not get user data, please login again');
    console.error('can not get user data, please login again', err);
    window.location.href = '/';
  }
};

export async function registerUserIdentity() {
  await registerMe();
}

export function values<K extends string | number | symbol, V>(
  object: Record<K, V> | Partial<Record<K, V>>,
): V[] {
  return Object.values(object) as V[];
}

export type PlaidWealthSnapshot = {
  snapshot_id: string;
  as_of: string;
  summary: {
    total_assets: number;
    total_liabilities: number;
    net_worth: number;
  };
  accounts: BankAccountData[];
  last_plaid_value_dates: Record<string, string | null>;
  chart: WealthChartData;
  chart_latest: {
    date: string | null;
    value: number | null;
  };
  snapshot_unavailable_reason?: 'none' | 'no_linked_accounts' | 'snapshot_unavailable';
};

type SnapshotRequestError = Error & {
  statusCode?: number;
  path?: string;
};

function parseRequiredNumber(value: unknown, fieldName: string): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  throw new Error(`Invalid snapshot field: ${fieldName}`);
}

function isOptionalFiniteNumberOrNull(value: unknown): value is number | null | undefined {
  return (
    value === undefined || value === null || (typeof value === 'number' && Number.isFinite(value))
  );
}

function isOptionalBoolean(value: unknown): value is boolean | undefined {
  return value === undefined || typeof value === 'boolean';
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalStringOrNull(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string';
}

function normalizeSnapshotUnavailableReason(
  value: unknown,
): PlaidWealthSnapshot['snapshot_unavailable_reason'] {
  if (value === 'none' || value === 'no_linked_accounts' || value === 'snapshot_unavailable') {
    return value;
  }
  return undefined;
}

function isFirmDataLinkedAccount(account: Record<string, unknown>): boolean {
  if (account.source === 'firm-data') {
    return true;
  }
  return typeof account.firm_id === 'string' && account.firm_id.trim() !== '';
}

function isValidItemIdForAccount(account: Record<string, unknown>): boolean {
  // Firm-data wealth accounts do not always carry a Plaid Item id on the read path.
  // Skip item_id validation for those so null/missing does not drop the account.
  if (isFirmDataLinkedAccount(account)) {
    return true;
  }
  // Rows with no Item (e.g. Apex custodial accounts) may send null; keep them, they
  // just cannot be unlinked or relinked.
  return isOptionalStringOrNull(account.item_id);
}

function isValidBankAccount(account: unknown): account is BankAccountData {
  if (!isRecord(account)) {
    return false;
  }

  if (
    typeof account.user_id !== 'string' ||
    // Deprecated (ENG-2425): the backend is dropping the Plaid access token, so it may
    // be absent or null. Accounts are identified by item_id instead.
    !isOptionalStringOrNull(account.access_token) ||
    typeof account.account_id !== 'string' ||
    typeof account.name !== 'string' ||
    typeof account.mask !== 'string' ||
    typeof account.account_type !== 'string' ||
    typeof account.institution_id !== 'string' ||
    (account.institution_logo !== null && typeof account.institution_logo !== 'string') ||
    typeof account.institution_name !== 'string'
  ) {
    return false;
  }

  if (
    account.asset_or_liability !== ASSET_LIABILITY.ASSET &&
    account.asset_or_liability !== ASSET_LIABILITY.LIABILITY
  ) {
    return false;
  }

  return (
    isOptionalFiniteNumberOrNull(account.current_balance) &&
    isOptionalFiniteNumberOrNull(account.available_balance) &&
    isOptionalStringOrNull(account.currency_code) &&
    isOptionalBoolean(account.has_investments) &&
    isOptionalBoolean(account.has_liabilities) &&
    isOptionalBoolean(account.has_transactions) &&
    isValidItemIdForAccount(account) &&
    isOptionalString(account.source) &&
    isOptionalStringOrNull(account.last_plaid_value_date) &&
    isOptionalStringOrNull(account.created_at) &&
    isOptionalStringOrNull(account.custodian_account_id) &&
    isOptionalStringOrNull(account.custodian_code) &&
    isOptionalStringOrNull(account.plaid_account_id)
  );
}

function normalizeSnapshotBankAccount(account: BankAccountData): BankAccountData {
  return {
    ...account,
    access_token: account.access_token || undefined,
    item_id: account.item_id == null || account.item_id === '' ? undefined : account.item_id,
    custodian_account_id:
      account.custodian_account_id == null ? undefined : account.custodian_account_id,
    custodian_code: account.custodian_code == null ? undefined : account.custodian_code,
    plaid_account_id:
      account.plaid_account_id == null || account.plaid_account_id === ''
        ? undefined
        : account.plaid_account_id,
  };
}

function normalizePlaidWealthSnapshot(raw: unknown): PlaidWealthSnapshot {
  if (!isRecord(raw)) {
    throw new Error('Invalid snapshot payload: expected object');
  }

  // Support both direct payload and { data: payload } wrappers.
  const payload = 'data' in raw && isRecord(raw.data) ? (raw.data as Record<string, unknown>) : raw;

  const summary = payload.summary;
  const chart = payload.chart;
  const chartLatest = payload.chart_latest;

  if (!isRecord(summary)) {
    throw new Error('Invalid snapshot payload: missing summary');
  }
  if (!isRecord(chart)) {
    throw new Error('Invalid snapshot payload: missing chart');
  }
  if (!Array.isArray(chart.timestamps) || !Array.isArray(chart.values)) {
    throw new Error('Invalid snapshot payload: malformed chart');
  }
  if (!Array.isArray(payload.accounts)) {
    throw new Error('Invalid snapshot payload: missing accounts');
  }

  const timestamps = chart.timestamps.map((ts, idx) => {
    const n = parseRequiredNumber(ts, `chart.timestamps[${idx}]`);
    return Math.trunc(n);
  });

  const values = chart.values.map((value) => String(value));

  const lastPlaidValueDates = isRecord(payload.last_plaid_value_dates)
    ? Object.fromEntries(
        Object.entries(payload.last_plaid_value_dates).map(([k, v]) => [
          k,
          v == null ? null : String(v),
        ]),
      )
    : {};

  const latest = isRecord(chartLatest) ? chartLatest : {};
  const latestValue =
    latest.value == null ? null : parseRequiredNumber(latest.value, 'chart_latest.value');
  const snapshotUnavailableReason = normalizeSnapshotUnavailableReason(
    payload.snapshot_unavailable_reason,
  );
  const accounts = payload.accounts.reduce<BankAccountData[]>((validAccounts, account, index) => {
    if (isValidBankAccount(account)) {
      validAccounts.push(normalizeSnapshotBankAccount(account));
    } else {
      console.warn(`Dropping invalid snapshot account payload at index ${index}`, account);
    }
    return validAccounts;
  }, []);

  return {
    snapshot_id: String(payload.snapshot_id ?? ''),
    as_of: String(payload.as_of ?? ''),
    summary: {
      total_assets: parseRequiredNumber(summary.total_assets, 'summary.total_assets'),
      total_liabilities: parseRequiredNumber(
        summary.total_liabilities,
        'summary.total_liabilities',
      ),
      net_worth: parseRequiredNumber(summary.net_worth, 'summary.net_worth'),
    },
    accounts,
    last_plaid_value_dates: lastPlaidValueDates,
    chart: {
      values,
      timestamps,
    },
    chart_latest: {
      date: latest.date == null ? null : String(latest.date),
      value: latestValue,
    },
    snapshot_unavailable_reason: snapshotUnavailableReason,
  };
}

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const err = error as SnapshotRequestError;
  if (err.statusCode === 404) {
    return true;
  }
  return /not found/i.test(error.message);
}

function isNoLinkedAccountsError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return /no linked|no external|no account|no accounts/i.test(error.message);
}

function isSnapshotUnavailableError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const err = error as SnapshotRequestError;
  if (err.statusCode === 404 || err.statusCode === 410) {
    return true;
  }
  return /not found|gone|no snapshot|not onboarded/i.test(error.message);
}

function createEmptyPlaidWealthSnapshot(
  reason: 'no_linked_accounts' | 'snapshot_unavailable',
): PlaidWealthSnapshot {
  return {
    snapshot_id: '',
    as_of: '',
    summary: {
      total_assets: 0,
      total_liabilities: 0,
      net_worth: 0,
    },
    accounts: [],
    last_plaid_value_dates: {},
    chart: {
      values: [],
      timestamps: [],
    },
    chart_latest: {
      date: null,
      value: null,
    },
    snapshot_unavailable_reason: reason,
  };
}

function summarizeInvalidFallbackAccountPayload(payload: unknown): {
  redacted_account_payload: true;
  payload_type: string;
  has_account_id_field: boolean;
  has_access_token_field: boolean;
  has_email_field: boolean;
  has_institution_metadata_field: boolean;
  account_type: string | null;
} {
  if (!isRecord(payload)) {
    return {
      redacted_account_payload: true,
      payload_type: Array.isArray(payload) ? 'array' : typeof payload,
      has_account_id_field: false,
      has_access_token_field: false,
      has_email_field: false,
      has_institution_metadata_field: false,
      account_type: null,
    };
  }

  const accountType = typeof payload.account_type === 'string' ? payload.account_type : null;

  return {
    redacted_account_payload: true,
    payload_type: 'object',
    has_account_id_field: 'account_id' in payload || 'id' in payload,
    has_access_token_field: 'access_token' in payload,
    has_email_field: 'email' in payload,
    has_institution_metadata_field:
      'institution' in payload || 'institution_id' in payload || 'institution_name' in payload,
    account_type: accountType,
  };
}

function normalizePlaidAccountsPayload(raw: unknown): BankAccountData[] {
  const extractAccountsArray = (value: unknown): unknown[] | null => {
    if (Array.isArray(value)) {
      return value;
    }
    if (!isRecord(value)) {
      return null;
    }
    if ('data' in value) {
      const data = value.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (isRecord(data) && Array.isArray(data.accounts)) {
        return data.accounts;
      }
    }
    if (Array.isArray(value.accounts)) {
      return value.accounts;
    }
    return null;
  };

  const accountsPayload = extractAccountsArray(raw);
  if (!accountsPayload) {
    throw new Error('Invalid accounts payload: missing accounts array');
  }

  return accountsPayload.reduce<BankAccountData[]>((validAccounts, account, idx) => {
    if (isValidBankAccount(account)) {
      validAccounts.push(normalizeSnapshotBankAccount(account));
    } else {
      console.warn('Dropping invalid fallback account payload', {
        index: idx,
        path: 'fallback_accounts',
        summary: summarizeInvalidFallbackAccountPayload(account),
      });
    }
    return validAccounts;
  }, []);
}

async function requestWealthSnapshotPath(
  path: string,
  apiName: string,
): Promise<PlaidWealthSnapshot> {
  try {
    const apiResponse = get({
      apiName,
      path,
    });
    const response = await apiResponse.response;
    const statusCode =
      typeof (response as { statusCode?: unknown }).statusCode === 'number'
        ? ((response as { statusCode: number }).statusCode as number)
        : 200;
    const body = await response.body.text();

    if (statusCode >= 400) {
      const detail = body || 'request failed';
      const err: SnapshotRequestError = new Error(
        `Snapshot request failed (${statusCode}) on ${path}: ${detail}`,
      );
      err.statusCode = statusCode;
      err.path = path;
      throw err;
    }

    if (!body) {
      throw new Error(`Snapshot response was empty for ${path}`);
    }
    const parsed = JSON.parse(body) as unknown;
    return normalizePlaidWealthSnapshot(parsed);
  } catch (error) {
    if (isAmplifyError(error)) {
      const status = error.response.status;
      const statusText = error.response.statusText || 'request failed';
      const err: SnapshotRequestError = new Error(
        `Snapshot request failed (${status}) on ${path}: ${statusText}`,
      );
      err.statusCode = status;
      err.path = path;
      throw err;
    }
    throw error;
  }
}

async function requestPlaidWealthSnapshotPath(path: string): Promise<PlaidWealthSnapshot> {
  const apiName = await getPlaidApiAlias();
  return requestWealthSnapshotPath(path, apiName);
}

async function requestPlaidAccountsPath(path: string): Promise<BankAccountData[]> {
  try {
    const apiName = await getPlaidApiAlias();
    const apiResponse = get({
      apiName,
      path,
    });
    const response = await apiResponse.response;
    const statusCode =
      typeof (response as { statusCode?: unknown }).statusCode === 'number'
        ? ((response as { statusCode: number }).statusCode as number)
        : 200;
    const body = await response.body.text();

    if (statusCode >= 400) {
      const detail = body || 'request failed';
      const err: SnapshotRequestError = new Error(
        `Accounts request failed (${statusCode}) on ${path}: ${detail}`,
      );
      err.statusCode = statusCode;
      err.path = path;
      throw err;
    }

    if (!body) {
      throw new Error(`Accounts response was empty for ${path}`);
    }

    const parsed = JSON.parse(body) as unknown;
    return normalizePlaidAccountsPayload(parsed);
  } catch (error) {
    if (isAmplifyError(error)) {
      const status = error.response.status;
      const statusText = error.response.statusText || 'request failed';
      const err: SnapshotRequestError = new Error(
        `Accounts request failed (${status}) on ${path}: ${statusText}`,
      );
      err.statusCode = status;
      err.path = path;
      throw err;
    }
    throw error;
  }
}

async function requestFallbackLinkedPlaidAccounts(): Promise<BankAccountData[]> {
  const primaryPath = '/external/accounts/me';
  const alternatePath = '/accounts/me';

  try {
    return await requestPlaidAccountsPath(primaryPath);
  } catch (error) {
    if (isNotFoundError(error)) {
      try {
        return await requestPlaidAccountsPath(alternatePath);
      } catch (alternateError) {
        if (
          isNotFoundError(alternateError) ||
          isNoLinkedAccountsError(alternateError) ||
          isSnapshotUnavailableError(alternateError)
        ) {
          return [];
        }
        throw alternateError;
      }
    }
    if (isNoLinkedAccountsError(error) || isSnapshotUnavailableError(error)) {
      return [];
    }
    throw error;
  }
}

async function hydrateSnapshotWithFallbackAccounts(
  snapshot: PlaidWealthSnapshot,
  allowAccountFallback: boolean,
): Promise<PlaidWealthSnapshot> {
  if (!allowAccountFallback || snapshot.accounts.length > 0) {
    return snapshot;
  }

  try {
    const fallbackAccounts = await requestFallbackLinkedPlaidAccounts();
    if (fallbackAccounts.length === 0) {
      if (snapshot.snapshot_unavailable_reason) {
        return snapshot;
      }
      return {
        ...snapshot,
        snapshot_unavailable_reason: 'no_linked_accounts',
      };
    }

    return {
      ...snapshot,
      accounts: fallbackAccounts,
      snapshot_unavailable_reason: 'snapshot_unavailable',
    };
  } catch (error) {
    console.warn('Unable to load fallback linked accounts for plaid wealth snapshot', error);
    if (snapshot.snapshot_unavailable_reason) {
      return snapshot;
    }
    return {
      ...snapshot,
      snapshot_unavailable_reason: 'snapshot_unavailable',
    };
  }
}

export const getPlaidWealthSnapshot = async (
  account_ids?: string[],
  range?: TimeRange,
  firmDataEnabled = false,
): Promise<PlaidWealthSnapshot> => {
  const queryParams = new URLSearchParams();
  account_ids?.forEach((id) => queryParams.append('account_ids', id));
  range && queryParams.append('range', range);
  const query = queryParams.toString();

  if (firmDataEnabled) {
    // Firm-data reads must never be routed through plaid-cash-integration:
    // go straight to wavvest-client-portal-backend's own wealth snapshot
    // route, which reads firm-data directly for firm-data-enabled investors.
    // No plaid-cash fallback here — normalizePlaidWealthSnapshot already
    // tolerates the firm-data account shape.
    const path = query ? `/api/wealth/snapshot?${query}` : `/api/wealth/snapshot`;
    return requestWealthSnapshotPath(path, 'ClientPortalAPI');
  }

  const allowAccountFallback = !account_ids || account_ids.length === 0;
  const primaryPath = query ? `/external/wealth/snapshot?${query}` : `/external/wealth/snapshot`;
  const alternatePath = query ? `/wealth/snapshot?${query}` : `/wealth/snapshot`;

  const createUnavailableSnapshot = async (
    reason: 'no_linked_accounts' | 'snapshot_unavailable',
  ): Promise<PlaidWealthSnapshot> =>
    hydrateSnapshotWithFallbackAccounts(
      createEmptyPlaidWealthSnapshot(reason),
      allowAccountFallback,
    );

  try {
    const snapshot = await requestPlaidWealthSnapshotPath(primaryPath);
    return await hydrateSnapshotWithFallbackAccounts(snapshot, allowAccountFallback);
  } catch (error) {
    if (isNotFoundError(error)) {
      try {
        const snapshot = await requestPlaidWealthSnapshotPath(alternatePath);
        return await hydrateSnapshotWithFallbackAccounts(snapshot, allowAccountFallback);
      } catch (alternateError) {
        if (isNoLinkedAccountsError(alternateError)) {
          console.warn(
            'Plaid snapshot unavailable for investor; returning empty snapshot',
            alternateError,
          );
          return await createUnavailableSnapshot('no_linked_accounts');
        }
        if (isSnapshotUnavailableError(alternateError)) {
          console.warn(
            'Plaid snapshot unavailable for investor; returning empty snapshot',
            alternateError,
          );
          return await createUnavailableSnapshot('snapshot_unavailable');
        }
        throw alternateError;
      }
    }
    if (isNoLinkedAccountsError(error)) {
      console.warn('Plaid snapshot unavailable for investor; returning empty snapshot', error);
      return await createUnavailableSnapshot('no_linked_accounts');
    }
    if (isSnapshotUnavailableError(error)) {
      console.warn('Plaid snapshot unavailable for investor; returning empty snapshot', error);
      return await createUnavailableSnapshot('snapshot_unavailable');
    }
    throw error;
  }
};

export const getPlaidHoldings = async (): Promise<PlaidHoldingsData> => {
  const url = `/external/investments/me`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiName = await getPlaidApiAlias();
        const apiResponse = get({
          apiName,
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as PlaidHoldingsData;
            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get plaid holdings data failed:', error);
        return reject(error);
      }
    })();
  });
};

export const getPlaidTransactions = async (): Promise<PlaidTransactionsData> => {
  const url = `/transactions/me/recent`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiName = await getPlaidApiAlias();
        const apiResponse = get({
          apiName,
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as PlaidTransactionsData;
            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get plaid transactions data failed:', error);
        return reject(error);
      }
    })();
  });
};
//   try {
//     const data = await PlaidApi.get<Record<string, BankAccountData>>(
//       "external/accounts/me",
//     );
//     return data;
//   } catch (e) {
//     if (isAmplifyError(e)) {
//       if (e.response.status === 410) {
//         return null;
//       }
//     }
//     throw e;
//   }
// }

// export async function registerNotification() {
//   const token = await getPushNotificationToken();
//   if (token) {
//     await registerDeviceToken(token);
//   }
// }

export const registerMe = async () => {
  const user = await getUserData();
  const url = `/api/register_me/${user?.cognito_id}`;

  const apiResponse = post({
    apiName: 'ClientPortalAPI',
    path: url,
  });

  const response = await apiResponse.response;
  const bodyText = await response.body.text();
  if (bodyText) return bodyText;
  return null;
};

export type FilehubFilesData = {
  key: string;
  filename: string;
  size_byte: number;
  upload_at: string;
  file_type: string;
  owner_cognito_id: string;
  owner_type: 'advisor' | 'enduser';
  is_public: boolean;
  requires_document_processing: boolean;
  document_processing_state: boolean;
  shares?: string[];
  client_id?: string;
};

export type FilehubFilesResponse = {
  files: FilehubFilesData[];
  pagination_key: string;
  total_count: number;
  current_page: number;
  total_pages: number;
  has_more: boolean;
};

export type uploadFilehubFileResponse = {
  file: FilehubFilesData;
};

export type DeleteFilehubFileResponse = {
  status: string;
  message: string;
  data: uploadFilehubFileResponse;
};

export type getFilehubPresignedURLResponse = {
  presigned_body: {
    url: string;
    fields: {
      [key: string]: string;
    };
  };
  data: FilehubFilesData;
};

export const getFilehubPresignedURL = async (
  params: {
    is_public: boolean;
    requires_document_processing: boolean;
    shares: string[];
    file_name: string;
    file_size: number;
    file_type: string;
  },
  file: File | RcFile,
): Promise<getFilehubPresignedURLResponse> => {
  const user = await getUserData();
  // const url = `/api/clients/${user?.advisor_info.client_id}/filehub/create`;
  const url = `/api/clients/${user?.client_id}/filehub/create`;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      if (!reader.result) {
        return reject(new Error('read file failed: result is empty'));
      }

      if (typeof reader.result !== 'string') {
        return reject(new Error('read file failed: result is not a string'));
      }

      const base64File = reader.result.split(',')[1];
      try {
        const apiResponse = post({
          apiName: 'ClientPortalAPI',
          path: url,
          options: {
            body: {
              owner_cognito_id: user?.cognito_id || '',
              is_public: params.is_public,
              requires_document_processing: params.requires_document_processing,
              owner_type: 'enduser',
              shares: params.shares,
              file_name: params.file_name,
              file_size: params.file_size,
              mime_type: params.file_type,
              file_content: base64File,
            },
            headers: { 'Content-Type': 'application/json' },
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: getFilehubPresignedURLResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('Failed to upload profile media:', error);
        return reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('read file failed'));
    };
  });
};

export type confirmFileResponse = {
  file: FilehubFilesData;
};

export const confirmUploadFile = async (
  file_id: string,
  file_name: string,
  owner_cognito_id: string,
  share_ids: string[],
): Promise<confirmFileResponse> => {
  const user = await getUserData();
  // const url = `/api/clients/${user?.advisor_info.client_id}/filehub/file/${file_id}/confirm`;
  const url = `/api/clients/${user?.client_id}/filehub/file/${file_id}/confirm`;
  const data = {
    file_name: file_name,
    owner_cognito_id: owner_cognito_id,
    share_ids: share_ids,
  };

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = post({
          apiName: 'ClientPortalAPI',
          path: url,
          options: {
            body: data,
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: confirmFileResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('confirm upload file failed:', error);
        return reject(error);
      }
    })();
  });
};

export const getAdvisorFiles = async (): Promise<FilehubFilesResponse> => {
  const user = await getUserData();
  // const url = `/api/clients/${user?.advisor_info.client_id}/filehub/owner/files`;
  const url = `/api/clients/${user?.client_id}/filehub/owner/files`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ClientPortalAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: FilehubFilesResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('Failed to fetch FilehubFiles data:', error);
        throw error;
      }
    })();
  });
};

export const getReceivedAdvisorFiles = async (): Promise<FilehubFilesResponse> => {
  const user = await getUserData();
  // const url = `/api/clients/${user?.advisor_info.client_id}/filehub/received/files`;
  const url = `/api/clients/${user?.client_id}/filehub/received/files`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ClientPortalAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: FilehubFilesResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get received advisor files failed:', error);
        return reject(error);
      }
    })();
  });
};

export const getAdvisorFilesWithShares = async (params?: {
  pagination_key?: string;
  limit?: string;
  include_share_details?: string;
  search_keyword?: string;
  sort_by?: 'upload_at' | 'filename' | 'share_count';
  sort_order?: 'asc' | 'desc';
}): Promise<FilehubFilesResponse> => {
  // const user = await getUserData();
  const url = `/api/filehub/owner/files-with-shares`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ClientPortalAPI',
          path: url,
          options: {
            queryParams: params,
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: FilehubFilesResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('Failed to get advisor files with shares:', error);
        return reject(error);
      }
    })();
  });
};

export const getReceivedAdvisorFilesWithShares = async (params?: {
  pagination_key?: string;
  limit?: string;
  include_share_details?: string;
  search_keyword?: string;
  sort_by?: 'upload_at' | 'filename' | 'share_count';
  sort_order?: 'asc' | 'desc';
}): Promise<FilehubFilesResponse> => {
  // const user = await getUserData();
  const url = `/api/filehub/received/files-with-shares`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ClientPortalAPI',
          path: url,
          options: {
            queryParams: params,
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: FilehubFilesResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('Failed to get received advisor files with shares:', error);
        return reject(error);
      }
    })();
  });
};

export const deleteEnduserFile = async (file_id: string): Promise<DeleteFilehubFileResponse> => {
  const user = await getUserData();
  // const url = `/api/clients/${user?.advisor_info.client_id}/filehub/enduser/files/${file_id}`;
  const url = `/api/clients/${user?.client_id}/filehub/enduser/files/${file_id}`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = del({
          apiName: 'ClientPortalAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();
        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: DeleteFilehubFileResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('delete advisor file failed:', error);
        return reject(error);
      }
    })();
  });
};

export type DownloadLinkResponse = {
  preview_url: string;
  download_url: string;
};

export const getSharedFileDownloadLink = async (file_id: string): Promise<DownloadLinkResponse> => {
  const user = await getUserData();
  // const url = `/api/clients/${user?.advisor_info.client_id}/filehub/enduser/shared_files/${file_id}`;
  const url = `/api/clients/${user?.client_id}/filehub/enduser/shared_files/${file_id}`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ClientPortalAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: DownloadLinkResponse;
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get shared file download link failed:', error);
        return reject(error);
      }
    })();
  });
};

export type ApexDocument = {
  document_id?: string;
  document_type?: string;
  document_name?: string;
  created_at?: string;
  download_uri?: string;
  download_link?: string;
  name?: string;
  correspondent_id?: string;
  document_date?: string;
  account_document?: {
    account_id: string;
    document_type: string;
    uploaded_date?: { year: number; month: number; day: number };
  };
  investor_document?: {
    account_id: string;
    document_type: string;
    process_date?: { year: number; month: number; day: number };
  };
  id_document?: {
    legal_natural_person_id: string;
    document_type: string;
    uploaded_date?: { year: number; month: number; day: number };
  };
};

export type ApexDocumentsResponse = {
  document_records: ApexDocument[];
  next_page_token?: string;
};

export const getApexDocuments = async (): Promise<ApexDocumentsResponse> => {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: '/accounts/me/docs',
        });

        const response = await apiResponse.response;
        const body = (await response.body.json()) as ApexDocumentsResponse;

        if (body && body.document_records) {
          return resolve(body);
        } else {
          return reject(new Error('Invalid APEX documents response'));
        }
      } catch (error) {
        console.error('Failed to fetch APEX documents:', error);
        return reject(error);
      }
    })();
  });
};

export type LinkTokenResponse = {
  link_token: string;
};

/**
 * Which existing bank login to open Plaid Link in update mode for. `itemId` is the
 * supported way; `accessToken` is a deprecated fallback (ENG-2425) for legacy rows
 * whose item_id is empty, and is only used when no item id is given. The backend
 * rejects requests that send both.
 *
 * `accountId` pins which login under the Item to update. Plaid's item_id is per
 * (user, institution), so one item_id can cover several logins/tokens; with
 * `?item_id=X` alone plaid-cash answers 409 when that is the case, and with
 * `?item_id=X&account_id=Y` it uses the token that account belongs to (404 if the
 * account is not the user's or not in that Item). It is only sent alongside an
 * item id — plaid-cash rejects account_id without item_id (400), and it is never
 * paired with access_token.
 */
export type PlaidLinkTokenTarget = { itemId?: string; accountId?: string; accessToken?: string };

const linkTokenQueryParams = (target?: PlaidLinkTokenTarget): Record<string, string> => {
  if (target?.itemId) {
    return target.accountId
      ? { item_id: target.itemId, account_id: target.accountId }
      : { item_id: target.itemId };
  }
  if (target?.accessToken) {
    return { access_token: target.accessToken };
  }
  return {};
};

/**
 * Creates a Plaid Link token. Pass an existing bank login to open Link in update
 * mode for it (re-authentication); omit it for a fresh link.
 */
export const createPlaidLinkToken = async (
  target?: PlaidLinkTokenTarget,
): Promise<LinkTokenResponse> => {
  const url = `/external/create_link_token`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiName = await getPlaidApiAlias();
        const apiResponse = put({
          apiName,
          path: url,
          options: {
            queryParams: linkTokenQueryParams(target),
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as LinkTokenResponse;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('create plaid link token failed:', error);
        return reject(error);
      }
    })();
  });
};

export const exchangePlaidPublicToken = async (
  publicToken: string,
): Promise<Record<string, unknown>> => {
  const url = `/external/exchange_token`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiName = await getPlaidApiAlias();
        const apiResponse = post({
          apiName,
          path: url,
          options: {
            body: { public_token: publicToken },
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as Record<string, unknown>;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('exchange plaid public token failed:', error);
        return reject(error);
      }
    })();
  });
};

export const getPlaidAggregatedData = async (
  sync: boolean = false,
): Promise<AggregatedFinancialData> => {
  const url = `/external/accounts/me/aggregated`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiName = await getPlaidApiAlias();
        const apiResponse = get({
          apiName,
          path: url,
          options: sync ? { queryParams: { sync: 'true' } } : undefined,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as AggregatedFinancialData;
            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get plaid aggregated data failed:', error);
        return reject(error);
      }
    })();
  });
};

const deletePlaidPath = async (path: string) => {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiName = await getPlaidApiAlias();
        const apiResponse = del({
          apiName,
          path,
        });
        const response = await apiResponse.response;
        const body = await response.body.text();
        if (body) {
          try {
            const responseData = JSON.parse(body);
            return resolve(responseData);
          } catch (jsonError) {
            return reject(jsonError);
          }
        }
        return reject(new Error('invalid response data'));
      } catch (error) {
        return reject(error);
      }
    })();
  });
};

/**
 * @deprecated ENG-2425: unlink by item id with `deletePlaidConnectionByItemId`. Only
 * for legacy rows whose item_id is empty; remove once the backend stops sending
 * access tokens.
 */
export const deletePlaidConnection = async (accessToken: string) =>
  deletePlaidPath(`/external/accounts/${accessToken}`);

export type DeletePlaidConnectionByItemResponse = {
  removed_account_ids: string[];
  removed_count: number;
};

/**
 * Unlinks a whole bank login by its Plaid item id. The server looks the access
 * token up itself, so this is the default way to unlink (ENG-2425).
 */
export const deletePlaidConnectionByItemId = async (itemId: string) =>
  deletePlaidPath(
    `/external/connections/${encodeURIComponent(itemId)}`,
  ) as Promise<DeletePlaidConnectionByItemResponse>;

export type GetAccountsV2Response = {
  accounts: Account[];
};

export const getApexData = async (firmDataEnabled = false): Promise<GetAccountsV2Response> => {
  if (firmDataEnabled) {
    const apiResponse = get({
      apiName: 'ClientPortalAPI',
      path: '/apex/v2/accounts',
    });
    const response = await apiResponse.response;
    const statusCode = getApiStatusCode(response);
    const body = await response.body.text();
    if (statusCode >= 400) {
      throw new Error(
        parsePublicApiErrorMessage(body, `Apex accounts request failed with status ${statusCode}`),
      );
    }
    const unwrapped = unwrapPublicApiBody<GetAccountsV2Response>(body);
    if (!Array.isArray(unwrapped?.accounts)) {
      throw new Error('invalid response data');
    }
    return unwrapped;
  }

  const url = `/v2/accounts`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as GetAccountsV2Response;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get apex data failed', error);
        return reject(error);
      }
    })();
  });
};

export type GetAccountDetailResponse = {
  account_detail: AccountDetail;
};

export const getAccountDetailData = async (
  account_id: string,
): Promise<GetAccountDetailResponse> => {
  const url = `/accounts/me/${account_id}/detail`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as GetAccountDetailResponse;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get account detail data failed', error);
        return reject(error);
      }
    })();
  });
};

export type GetAccountFinancialDetailResponse = {
  cash_available_for_withdraw: string;
  ira_contribution_constraints?: GetIraContributionConstraintsResponse;
  ira_distribution_constraints?: GetIraDistributionConstraintsResponse;
};

export const getAccountFinancialDetailData = async (
  account_id: string,
): Promise<GetAccountFinancialDetailResponse> => {
  const url = `/accounts/me/${account_id}/financial-details`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as GetAccountFinancialDetailResponse;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get account detail data failed', error);
        return reject(error);
      }
    })();
  });
};

export type GetHoldingsResponse = {
  holdings: components['schemas']['Position'][];
};

export const getHoldingsData = async (account_id: string): Promise<GetHoldingsResponse> => {
  const url = `/v2/accounts/${account_id}/holdings`;
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as GetHoldingsResponse;
            return resolve(responseData);
          } catch (jsonError) {
            return reject(jsonError);
          }
        }
        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get holdings failed', error);
        return reject(error);
      }
    })();
  });
};

export type TimeRange = '1W' | '1M' | '3M' | '1Y' | 'YTD' | 'ALL';

export const getApexChartData = async (
  account_ids?: string[],
  range?: TimeRange,
): Promise<WealthChartData> => {
  const url = `/v2/accounts/charts`;
  const queryParams = new URLSearchParams();
  account_ids?.forEach((id) => queryParams.append('account_ids', id));
  range && queryParams.append('range', range);

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: `${url}?${queryParams.toString()}`,
          // options: {
          //   queryParams: Object.fromEntries(queryParams),
          // },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as WealthChartData;
            return resolve(responseData);
          } catch (jsonError) {
            return reject(jsonError);
          }
        }
        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get apex chart data failed', error);
        return reject(error);
      }
    })();
  });
};

// 单独的图表数据获取函数
export const getApexChartDataV2 = async (
  accountIds: string[],
  range: TimeRange = 'ALL',
): Promise<{ timestamp: number; value: number }[]> => {
  try {
    const chartsData = await getApexChartData(accountIds, range);

    if (!chartsData.timestamps || !chartsData.values) {
      return [];
    }

    // 转换为性能跟踪器所需的格式
    return chartsData.timestamps.map((timestamp: number, index: number) => ({
      timestamp: timestamp,
      value: parseFloat(chartsData.values[index] || '0'),
    }));
  } catch (error) {
    console.warn('Failed to fetch chart data:', error);
    return [];
  }
};

export const getApexAssetMapData = async (): Promise<AssetMap> => {
  const url = `/portfolios/asset-map`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as AssetMap;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get apex asset map data failed', error);
        return reject(error);
      }
    })();
  });
};

export type GetApexAccountAllocationResponse = components['schemas']['AssetAllocationOut'];

export const getApexAccountAllocation = async (
  account_id: string,
): Promise<GetApexAccountAllocationResponse> => {
  const url = `/accounts/${account_id}/asset-allocation`;
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
        });
        const response = await apiResponse.response;
        const body = await response.body.text();
        const responseData = JSON.parse(body) as GetApexAccountAllocationResponse;
        return resolve(responseData);
      } catch (error) {
        console.error('get apex account allocation failed', error);
        return reject(error);
      }
    })();
  });
};

export type GetDepositsResponse =
  components['schemas']['fo_apex_backend__routes__cash__GetDepositsResponse__Entry'][];
export type GetWithdrawalsResponse =
  components['schemas']['fo_apex_backend__routes__cash__GetWithdrawalsResponse__Entry'][];

export const getApexDeposits = async (account_id: string): Promise<GetDepositsResponse> => {
  const url = `/cash/${account_id}/deposits`;
  const queryParams = {
    max_count: '16',
    last_timestamp: new Date().toISOString(),
  };
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
          options: {
            queryParams,
          },
        });
        const response = await apiResponse.response;
        const body = await response.body.text();
        const responseData = JSON.parse(body) as {
          deposits: GetDepositsResponse;
        };
        return resolve(responseData.deposits || []);
      } catch (error) {
        console.error('get apex deposits failed', error);
        return reject(error);
      }
    })();
  });
};

// One transfer as firm-data records it. Direction lives on
// `transaction_type`, and `amount` is a positive magnitude for both
// directions. `status` is whatever the custodian last reported and is null
// until it reports anything -- null means unreported, not pending, and the
// Status column renders a dash for it.
export type FirmDataTransaction = {
  transaction_id: string;
  account_id: string;
  transaction_type: string;
  amount: string;
  trade_date: string;
  settle_date?: string | null;
  external_transfer_id?: string | null;
  status?: string | null;
};

/**
 * Whether one decoded row is safe for toTransferList to read.
 *
 * Takes `unknown` rather than FirmDataTransaction on purpose: the declared
 * type describes what the endpoint promises, and this is the boundary where
 * that promise is checked rather than assumed.
 *
 * `transaction_type` is the one field toTransferList reads off every row, and
 * it calls `.trim()` on it — so a truthy non-string there throws and takes the
 * whole transfer list with it. Absent is fine: toTransferList already treats a
 * row it cannot place as one to drop.
 */
const isReadableTransaction = (transaction: unknown): boolean => {
  if (typeof transaction !== 'object' || transaction === null) {
    return false;
  }
  const { transaction_type: transactionType } = transaction as Record<string, unknown>;
  return transactionType === undefined || typeof transactionType === 'string';
};

/**
 * Transfer history for one firm-data account, read from the client-portal
 * backend's own firm-data route rather than per-account Apex calls.
 *
 * `accountId` is firm-data's account id (`plaidAccounts[].account_id`), not
 * the Apex custodian id that fo-apex-backend endpoints take — this endpoint
 * belongs to firm-data, so it keys accounts the way firm-data does.
 */
export const getFirmDataAccountTransactions = async (
  accountId?: string,
): Promise<FirmDataTransaction[]> => {
  const apiResponse = get({
    apiName: 'ClientPortalAPI',
    path: '/api/wealth/transactions',
    ...(accountId ? { options: { queryParams: { account_id: accountId } } } : {}),
  });
  const response = await apiResponse.response;
  const statusCode = getApiStatusCode(response);
  const body = await response.body.text();

  // 404 is this route's "this firm is not on firm-data" answer, not a
  // failure. Callers only reach it when the firm-data flag says otherwise,
  // so treat it as "nothing recorded" rather than surfacing an error.
  if (statusCode === 404) {
    return [];
  }

  if (statusCode >= 400) {
    throw new Error(
      parsePublicApiErrorMessage(body, `Transactions request failed with status ${statusCode}`),
    );
  }

  const unwrapped = unwrapPublicApiBody<{ transactions?: FirmDataTransaction[] }>(body);
  if (!Array.isArray(unwrapped?.transactions)) {
    throw new Error('invalid response data');
  }
  // One unreadable row would otherwise take down the whole wealth page rather
  // than just itself.
  if (!unwrapped.transactions.every(isReadableTransaction)) {
    throw new Error('invalid response data');
  }
  return unwrapped.transactions;
};

/**
 * Whether one decoded row is safe for toApexHoldings to read.
 *
 * Takes `unknown` rather than FirmDataHolding on purpose: the declared type
 * describes what the endpoint promises, and this is the boundary where that
 * promise is checked rather than assumed.
 *
 * Every field the mapper copies onto a HoldingPosition is checked here, so
 * the shape it hands the wealth page matches its declared return type.
 * `instrument_id` carries the most weight: it becomes `asset_id`, which
 * mergeHoldings groups positions by, so a non-string there would quietly
 * mis-group holdings rather than fail loudly.
 *
 * Absent is fine for the numeric fields: the mapper already treats a missing
 * quantity or market value as zero.
 */
const isReadableHolding = (holding: unknown): boolean => {
  if (typeof holding !== 'object' || holding === null) {
    return false;
  }
  const row = holding as Record<string, unknown>;
  return (
    typeof row.instrument_id === 'string' &&
    (row.as_of_date === undefined ||
      row.as_of_date === null ||
      typeof row.as_of_date === 'string') &&
    (row.quantity === undefined || typeof row.quantity === 'string') &&
    (row.market_value === undefined || typeof row.market_value === 'string') &&
    (row.symbol === undefined || row.symbol === null || typeof row.symbol === 'string') &&
    (row.name === undefined || row.name === null || typeof row.name === 'string') &&
    (row.asset_class === undefined ||
      row.asset_class === null ||
      typeof row.asset_class === 'string')
  );
};

export type FirmDataHolding = {
  holding_id: string;
  account_id: string;
  instrument_id: string;
  // Optional because isReadableHolding accepts rows without them and the
  // mapper defaults both to zero — declaring them required would promise
  // callers something this boundary does not actually enforce.
  quantity?: string;
  market_value?: string;
  cost_basis?: string | null;
  cost_basis_metadata?: unknown;
  as_of_date?: string | null;
  symbol?: string | null;
  name?: string | null;
  asset_class?: string | null;
};

/**
 * Holdings for one firm-data account, read from the client-portal backend's
 * own firm-data route rather than per-account Apex calls.
 *
 * `accountId` is firm-data's account id (`plaidAccounts[].account_id`), not
 * the Apex custodian id that fo-apex-backend endpoints take — this endpoint
 * belongs to firm-data, so it keys accounts the way firm-data does.
 */
export const getFirmDataAccountHoldings = async (
  accountId?: string,
): Promise<FirmDataHolding[]> => {
  const apiResponse = get({
    apiName: 'ClientPortalAPI',
    path: '/api/wealth/holdings',
    ...(accountId ? { options: { queryParams: { account_id: accountId } } } : {}),
  });
  const response = await apiResponse.response;
  const statusCode = getApiStatusCode(response);
  const body = await response.body.text();

  // 404 is this route's "this firm is not on firm-data" answer, not a
  // failure. Callers only reach it when the firm-data flag says otherwise,
  // so treat it as "nothing recorded" rather than surfacing an error.
  if (statusCode === 404) {
    return [];
  }

  if (statusCode >= 400) {
    throw new Error(
      parsePublicApiErrorMessage(body, `Holdings request failed with status ${statusCode}`),
    );
  }

  const unwrapped = unwrapPublicApiBody<{ holdings?: FirmDataHolding[] }>(body);
  if (!Array.isArray(unwrapped?.holdings)) {
    throw new Error('invalid response data');
  }
  if (!unwrapped.holdings.every(isReadableHolding)) {
    throw new Error('invalid response data');
  }
  return unwrapped.holdings;
};

export const getApexWithdrawals = async (account_id: string): Promise<GetWithdrawalsResponse> => {
  const url = `/cash/${account_id}/withdrawals`;
  const queryParams = {
    max_count: '16',
    last_timestamp: new Date().toISOString(),
  };
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ApexAPI',
          path: url,
          options: {
            queryParams,
          },
        });
        const response = await apiResponse.response;
        const body = await response.body.text();
        const responseData = JSON.parse(body) as {
          withdrawals: GetWithdrawalsResponse;
        };
        return resolve(responseData.withdrawals || []);
      } catch (error) {
        console.error('get apex withdrawals failed', error);
        return reject(error);
      }
    })();
  });
};

export type PlanData = {
  id: string;
  sharedBy: string;
  sharedAt: number;
  visualizationHtml: {
    fileName: string;
    presignedUrl: string;
    sourceCode: string;
  };
  title: string;
  userAnswers: Record<string, any>;
};

export const getVisualizationData = async (): Promise<PlanData[]> => {
  const url = `/api/shared/visualization`;

  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const apiResponse = get({
          apiName: 'ClientPortalAPI',
          path: url,
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as {
              status: string;
              message: string;
              data: PlanData[];
            };

            if (responseData && responseData.data) {
              return resolve(responseData.data);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('get visualization data failed:', error);
        return reject(error);
      }
    })();
  });
};

export async function fetchAccountBankRelationships(
  accountId: string,
): Promise<GetBankRelationshipResponse> {
  try {
    const url = `/accounts/me/${accountId}/bank-relationships`;
    const apiResponse = get({
      apiName: 'ApexAPI',
      path: url,
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    const responseData = JSON.parse(body) as GetBankRelationshipResponse;
    return responseData;
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error signing agreements: ', error.response.status);
      throw error;
    } else {
      console.error('Unknown error signing agreements: ', error);
      throw error;
    }
  }
}

export async function fetchBankLinkOptions(): Promise<BankLinkOptionsResponse> {
  try {
    const apiResponse = get({
      apiName: 'ApexAPI',
      path: '/accounts/me/bank-link-options',
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    return readOkJson<BankLinkOptionsResponse>(
      getApiStatusCode(response),
      body,
      'Failed to load bank link options',
    );
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error fetching bank link options: ', error.response.status);
      throw new Error(
        messageFromAmplifyFailure(error.response, 'Failed to load bank link options'),
      );
    }
    console.error('Unknown error fetching bank link options: ', error);
    throw error;
  }
}

export async function fetchBankUnlinkCheck(
  plaidIds: readonly string[],
): Promise<BankUnlinkCheckResponse> {
  try {
    const apiResponse = post({
      apiName: 'ApexAPI',
      path: '/accounts/me/bank-unlink-check',
      options: {
        body: { plaid_ids: [...plaidIds] },
      },
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    return readOkJson<BankUnlinkCheckResponse>(
      getApiStatusCode(response),
      body,
      'Failed to check this bank before unlinking',
    );
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error checking bank unlink: ', error.response.status);
      throw new Error(
        messageFromAmplifyFailure(error.response, 'Failed to check this bank before unlinking'),
      );
    }
    console.error('Unknown error checking bank unlink: ', error);
    throw error;
  }
}

export async function assignBankRelationship(
  accountId: string,
  plaidId: string,
): Promise<AssignBankRelationshipResponse> {
  try {
    const apiResponse = post({
      apiName: 'ApexAPI',
      path: `/accounts/me/${accountId}/bank-relationships/assign`,
      options: {
        body: { plaid_id: plaidId },
      },
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    return readOkJson<AssignBankRelationshipResponse>(
      getApiStatusCode(response),
      body,
      'Failed to connect this bank to the wallet',
    );
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error assigning bank relationship: ', error.response.status);
      throw new Error(
        messageFromAmplifyFailure(error.response, 'Failed to connect this bank to the wallet'),
      );
    }
    console.error('Unknown error assigning bank relationship: ', error);
    throw error;
  }
}

export async function updateBankRelationship(
  accountId: string,
  plaidId: string,
): Promise<AssignBankRelationshipResponse> {
  try {
    const apiResponse = post({
      apiName: 'ApexAPI',
      path: `/accounts/me/${accountId}/bank-relationships/update`,
      options: {
        body: { plaid_id: plaidId },
      },
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    return readOkJson<AssignBankRelationshipResponse>(
      getApiStatusCode(response),
      body,
      'Failed to update the funding bank',
    );
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error updating bank relationship: ', error.response.status);
      throw new Error(
        messageFromAmplifyFailure(error.response, 'Failed to update the funding bank'),
      );
    }
    console.error('Unknown error updating bank relationship: ', error);
    throw error;
  }
}

export async function makeDeposits(request: MakeDepositsRequest[]): Promise<MakeDepositsResponse> {
  try {
    const url = `/cash/deposits`;
    const apiResponse = put({
      apiName: 'ApexAPI',
      path: url,
      options: {
        body: request,
      },
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    const responseData = JSON.parse(body) as MakeDepositsResponse;
    return responseData;
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error making deposit: ', error.response.status);
      throw error;
    } else {
      console.error('Unknown error making deposit: ', error);
      throw error;
    }
  }
}

export async function makeWithdrawals(
  request: MakeWithdrawalsRequest[],
): Promise<MakeWithdrawalsResponse> {
  try {
    const url = `/cash/withdrawals`;
    const apiResponse = put({
      apiName: 'ApexAPI',
      path: url,
      options: {
        body: request,
      },
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    const responseData = JSON.parse(body) as MakeWithdrawalsResponse;
    return responseData;
  } catch (error) {
    if (isAmplifyError(error)) {
      console.error('Error making withdrawal: ', error.response.status);
      throw error;
    } else {
      console.error('Unknown error making withdrawal: ', error);
      throw error;
    }
  }
}

export async function createNaturalPersonAndAccounts(
  userInfo: ApexUserInformation,
  enabledAccountTypes: AccountType[],
): Promise<CreateAccountsResponse> {
  return new Promise((resolve, reject) => {
    (async () => {
      let naturalPerson: components['schemas']['CreateNaturalPersonRequest'];
      try {
        naturalPerson = adaptNaturalPerson(userInfo);
      } catch (error) {
        console.error('Error converting natural person data: ', error);
        throw error;
      }

      const user = await getUserData();

      let account;

      try {
        account = adaptAccount(userInfo, user?.email ?? '');
      } catch (error) {
        console.error('Error converting account data: ', error);
        throw error;
      }

      const request: CreateAccountAndNaturalPersonalRequest = {
        account: account,
        natural_person: naturalPerson,
        enabled_accounts: enabledAccountTypes,
      };
      const url = `/accounts/me`;

      try {
        const apiResponse = put({
          apiName: 'ApexAPI',
          path: url,
          options: {
            body: request,
          },
        });

        const response = await apiResponse.response;
        const body = await response.body.text();

        if (body) {
          try {
            const responseData = JSON.parse(body) as CreateAccountsResponse;

            if (responseData) {
              return resolve(responseData);
            } else {
              return reject(new Error('response data missing data field'));
            }
          } catch (jsonError) {
            console.error('JSON parse error:', jsonError);
            return reject(jsonError);
          }
        }

        return reject(new Error('invalid response data'));
      } catch (error) {
        console.error('create natural person and accounts failed', error);
        return reject(error);
      }
    })();
  });
}

export async function signApexAgreements(request: SignAgreementsRequest) {
  try {
    const apiResponse = post({
      apiName: 'ApexAPI',
      path: '/accounts/me/sign-agreements',
      options: {
        body: request,
      },
    });

    const response = await apiResponse.response;
    if (response.statusCode === 204) {
      return;
    }
  } catch (error) {
    console.log('signAgreements', error);
    if (isAmplifyError(error)) {
      console.error('Error signing agreements: ', error.response.status);
      throw error;
    } else {
      console.error('Unknown error signing agreements: ', error);
      throw error;
    }
  }
}

export async function setAssetAllocation(accountId: string, data: SetAssetAllocationRequest) {
  try {
    const apiResponse = put({
      apiName: 'ApexAPI',
      path: `/accounts/${accountId}/asset-allocation`,
      options: {
        body: data,
      },
    });

    const response = await apiResponse.response;
    const body = await response.body.text();
    // A failed request already threw; the endpoint may succeed with no body.
    return body ? JSON.parse(body) : null;
  } catch (error) {
    console.error('Error when setting asset allocation for account: ', error);
    throw error;
  }
}

export async function addAccounts(data: AddAccountRequest): Promise<CreateAccountsResponse> {
  try {
    const apiResponse = post({
      apiName: 'ApexAPI',
      path: '/accounts/me/add-accounts',
      options: {
        body: data,
      },
    });

    const response = await apiResponse.response;
    const body = await response.body.text();

    if (body) {
      return JSON.parse(body) as CreateAccountsResponse;
    } else {
      throw new Error('Empty response body');
    }
  } catch (error) {
    console.error('Error adding accounts', error);
    throw error;
  }
}

export const updateVisualizationState = async (
  visualizationId: string,
  userAnswers: Record<string, any>,
) => {
  const url = `/api/shared/visualization/${visualizationId}/state`;
  try {
    const apiResponse = put({
      apiName: 'ClientPortalAPI',
      path: url,
      options: {
        body: {
          userAnswers,
        },
      },
    });

    await apiResponse.response;
  } catch (error) {
    console.error('get visualization data failed:', error);
    throw error;
  }
};

export type VerificationStatusResponse = {
  status: string;
};

export type VerificationResultResponse = {
  address: {
    summary: string;
    value: {
      city: string;
      country: string;
      postal_code: string;
      region: string;
      street: string;
      street2: string | null;
    };
  };
  date_of_birth: {
    summary: string;
    value: string;
  };
  document_check: string | null;
  name: {
    summary: string;
    value: {
      first_name: string;
      last_name: string;
    };
  };
  phone_number: {
    summary: string;
    value: string;
  };
  status: string;
  tax_id: {
    summary: string;
    value: string | null;
  };
};

export async function getVerificationStatus(): Promise<VerificationStatusResponse> {
  try {
    // return await IdentityApi.get("verification/status");
    const url = `/verification/status`;
    const apiName = await getIdentityApiAlias();
    const apiResponse = get({
      apiName,
      path: url,
      options: {
        queryParams: {
          _t: Date.now().toString(), // add timestamp to prevent cache
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      },
    });

    const response = await apiResponse.response;
    const body = await response.body.text();

    if (body) {
      return JSON.parse(body) as VerificationStatusResponse;
    } else {
      throw new Error('Empty response body');
    }
  } catch (e) {
    if (isAmplifyError(e)) {
      return { status: 'error' };
    }
    throw e;
  }
}

export async function getVerificationResult(): Promise<VerificationResultResponse | null> {
  try {
    // return await IdentityApi.get("verification/result");
    const url = `/verification/result`;
    const apiName = await getIdentityApiAlias();
    const apiResponse = get({
      apiName,
      path: url,
      options: {
        queryParams: {
          _t: Date.now().toString(), // add timestamp to prevent cache
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      },
    });

    const response = await apiResponse.response;
    const body = await response.body.text();

    if (body) {
      return JSON.parse(body) as VerificationResultResponse;
    } else {
      throw new Error('Empty response body');
    }
  } catch (e) {
    if (isAmplifyError(e)) {
      if (e.response.status === 410) {
        return null;
      }
    }
    throw e;
  }
}

export async function retryIdentity(): Promise<void> {
  try {
    const url = `/retry`;
    const op = post({
      apiName: 'ClientPortalAPI',
      path: url,
    });
    await op.response;
  } catch (e) {
    // swallow; safe to ignore retry failure and let subsequent steps surface errors
    console.warn('identity retry failed', e);
  }
}

export type CreateLinkTokenResponse = {
  link_token: string;
};

export type CreateLinkTokenBody = {
  first_name: string;
  last_name: string;
  email?: string;
  date_of_birth: string; // YYYY-MM-DD
  phone_number?: string;
  street?: string;
  city?: string;
  state?: string;
  zip_code?: string;
};

export async function createLinkToken(
  userInfo?: CreateLinkTokenBody,
): Promise<CreateLinkTokenResponse> {
  try {
    const url = `/create_link_token`;
    const apiName = await getIdentityApiAlias();
    const apiResponse = put({
      apiName,
      path: url,
      ...(userInfo ? { options: { body: userInfo } } : {}),
    });
    const response = await apiResponse.response;
    const body = await response.body.text();
    if (body) {
      return JSON.parse(body) as CreateLinkTokenResponse;
    } else {
      throw new Error('Empty response body');
    }
  } catch (e) {
    throw e;
  }
}

export async function getEndpointConfig(): Promise<ResourcesConfig> {
  return Amplify.getConfig() as ResourcesConfig;
}

export type PortfolioDataRequest = {
  portfolios: Record<string, Portfolio>;
};

export async function storePortfolioData(portfolios: Record<string, Portfolio>): Promise<void> {
  try {
    const user = await getUserData();
    if (!user) {
      throw new Error('User not found');
    }
    const url = `/api/portfolio-data`;

    const requestBody: PortfolioDataRequest = {
      portfolios,
    };

    const apiResponse = post({
      apiName: 'ClientPortalAPI',
      path: url,
      options: {
        body: requestBody,
      },
    });

    const response = await apiResponse.response;
    const body = await response.body.text();

    if (body) {
      return JSON.parse(body);
    } else {
      throw new Error('Empty response body');
    }
  } catch (error) {
    console.error('Error storing portfolio data: ', error);
    throw error;
  }
}

export type GetPortfolioDataResponse = {
  status: string;
  message: string;
  data: FinancialPlansState;
};

export async function getPortfolioData(): Promise<FinancialPlansState> {
  try {
    const user = await getUserData();
    if (!user) {
      throw new Error('User not found');
    }
    const url = `/api/portfolio-data`;

    const apiResponse = get({
      apiName: 'ClientPortalAPI',
      path: url,
    });

    const response = await apiResponse.response;
    const body = await response.body.text();
    if (body) {
      const responseData = JSON.parse(body) as GetPortfolioDataResponse;
      // Return the actual data array, not the wrapper object
      return responseData.data;
    } else {
      throw new Error('Empty response body');
    }
  } catch (error) {
    console.error('Error getting portfolio data: ', error);
    throw error;
  }
}
